<template lang="pug">
  CrmLeadDetailUi(
    v-if="lead"
    :lead="lead"
    :columns="columns"
    :users="users"
    :serviceTypes="serviceTypes"
    :canEditClient="canEditClient"
    @addProject="addProject"
    @changeStage="changeStage"
    @changeManager="changeManager"
    @editClient="editClient"
    @changeServiceType="changeServiceType"
    @saveDescription="saveDescription"
    @changeFiles="changeFiles"
    @removeFile="removeFile"
    @openReminders="openReminders"
    )
    //- Лента событий — свой контейнер со своими запросами, поэтому её
    //- рендерим здесь: CrmLeadDetailUi отвечает только за вёрстку и место
    //- под неё в сетке не заполняет само
    template(#timeline)
      CrmLeadDetailHistoryApi(
        :leadId="raw.id"
        :createdAt="raw.createdAt"
        :stages="stages"
        :canModerate="canManage"
        :reloadToken="reloadToken"
        )
</template>

<script>
import crmModel from '@/model/crm';
import popupModel from '@/model/popup';
import dirModel from '@/model/dir';
import form from '@/utils/form';
import utils from '@/utils/common';

import crmLeadApi from '@/api/crmLead';
import crmServiceTypeApi from '@/api/crmServiceType';

import CrmClientDir from '@/config/dir/CrmClient';

import CrmLeadDetailUi from '@/components/crm/CrmLeadDetailUi.vue';
import CrmLeadDetailHistoryApi from '@/components/crm/CrmLeadDetailHistoryApi.vue';
import BaseFormPopupWrappable from '@/components/base/BaseFormPopupWrappable.vue';
import UserSelectPopupWrappable from '@/components/UserSelectPopupWrappable.vue';
import CrmProjectDepartmentPopupWrappable from '@/components/crm/CrmProjectDepartmentPopupWrappable.vue';
import CrmLeadReminderPopupWrappable from '@/components/crm/CrmLeadReminderPopupWrappable.vue';

export default {
  name: 'CrmLeadDetailApi',
  components: { CrmLeadDetailUi, CrmLeadDetailHistoryApi },
  // конфиг справочника не реактивен, поэтому живёт в $options, а не в data
  clientDir: new CrmClientDir(),
  props: {
    leadId: {
      type: [Number, String],
      required: true,
    },
    companyId: {
      type: [Number, String],
      required: true,
    },
    // этапы воронки со страницы — истории они нужны только для раскраски
    // плашек смены этапа: в самой записи цвета нет
    stages: {
      type: Array,
      default: () => [],
    },
    // причины отказа воронки — нужны при переводе в колонку «Отказ»
    reasons: {
      type: Array,
      default: () => [],
    },
    // кого можно назначить ответственным
    users: {
      type: Array,
      default: () => [],
    },
    // типы услуг воронки — из них выбирают в самой заявке
    serviceTypes: {
      type: Array,
      default: () => [],
    },
    // владелец компании или заместитель: правка любого клиента и модерация
    // чужих комментариев
    canManage: Boolean,
  },
  data() {
    return {
      raw: null,
      // растёт после смены этапа или ответственного: сервер дописывает
      // событие в ленту, и историю нужно перечитать
      reloadToken: 0,
    };
  },
  created() {
    this.load();
  },
  computed: {
    /**
     * Заявка в том виде, в каком её показывает вёрстка. Сырой ответ (raw)
     * остаётся здесь: в GET одной заявки все связи приходят развёрнутыми
     * объектами, включая manager и projects — в списке заявок проектов нет
     * вовсе, и ленте событий нужны именно сырые id и дата.
     */
    lead() {
      if (!this.raw) return null;

      return {
        name: this.raw.title,
        createdAt: utils.convertDateToString(this.raw.createdAt, 'numeric', true),
        // ключ колонки, а не этапа: закрытая заявка стоит в завершающей
        columnId: crmModel.leadColumnKey(this.raw),
        rejectionReason: this.raw.rejectionReason?.name ?? null,
        client: this.raw.client?.name ?? '',
        phone: this.raw.client?.phone ?? '',
        email: this.raw.client?.email ?? null,
        // день рождения без времени, поэтому и показываем одну дату
        birthday: utils.convertDateToString(this.raw.client?.dateBirth, 'numeric'),
        author: this.raw.manager,
        projects: this.projects,
        // serviceType сервер отдаёт развёрнутым объектом — плашке нужен
        // только id выбранного
        serviceTypeId: this.raw.serviceType?.id ?? null,
        description: this.raw.description ?? '',
        files: this.raw.files ?? [],
      };
    },
    /**
     * Выбирать в деталке можно то же, что и на доске: этапы воронки плюс
     * завершающие колонки — иначе заявку отсюда нельзя было бы ни закрыть,
     * ни отправить в отказ.
     */
    columns() {
      return crmModel.boardColumns(this.stages);
    },
    canEditClient() {
      return crmModel.canEditClient(this.raw?.client, {
        isManager: this.canManage,
        user: this.$store.state.auth.user,
      });
    },
    projects() {
      return (this.raw?.projects ?? []).map((project) => {
        const status = project.isArchived
          ? { value: 'archived', label: 'В архиве', color: '#99A3C5' }
          : { value: 'active', label: 'В работе', color: '#5AC169' };

        return {
          id: project.id,
          name: project.name,
          // BaseStatus рисует выбранный элемент списка; выбирать здесь не из
          // чего, поэтому список из одного статуса
          statusItems: [status],
          statusValue: status.value,
        };
      });
    },
  },
  methods: {
    async load() {
      this.raw = await crmModel.run(
        () => crmModel.fetchLead(this.leadId),
        'Не удалось загрузить заявку',
      );
      if (this.raw) this.viewNotifications();
    },
    /**
     * Открыли заявку — её уведомления (напоминания, комментарии) прочитаны. Как в деталке задачи
     * (TaskDetail): с задержкой, чтобы счётчик на карточке не пропадал в
     * момент открытия шторки, а пользователь успел заметить, что он был.
     */
    viewNotifications() {
      setTimeout(() => {
        const getter = this.$store.getters['notification/itemIdsByFilter'];
        getter({ leadId: this.leadId })
          .forEach(n => this.$store.dispatch('notification/view', n));
      }, 3000);
    },
    /**
     * Смена колонки с деталки — то же самое, что перетаскивание карточки по
     * доске: этап, закрытие сделки или возврат в работу выбираются по самой
     * колонке (см. crmModel.applyLeadColumn).
     */
    async changeStage(columnId) {
      const column = this.columns.find(item => item.id === columnId);
      if (!column) return;

      // причину отказа спрашиваем до запроса: без неё бэк вернёт 422, а
      // отказ отвечать не должен менять заявку вовсе
      let rejectionReason = null;
      if (column.closing && column.isSuccessful === false) {
        rejectionReason = await crmModel.askRejectionReason(this.reasons);
        if (!rejectionReason) return;
      }

      const updated = await crmModel.applyLeadColumn(this.raw, column, rejectionReason);
      if (!updated) return;

      await this.load();
      this.reloadToken += 1;
    },

    /**
     * Тип услуги, описание и вложения правятся одним и тем же PATCH'ем и
     * отличаются только полем.
     *
     * После успешного запроса правим у себя ровно то, что сами и отправили
     * (local) — в том виде, в каком этого ждёт вёрстка: развёрнутым объектом,
     * а не IRI, ушедшим в params. Ответ на PATCH на место заявки не кладём: он
     * беднее, чем GET одной заявки — manager и stage приходят в нём голыми
     * IRI, а description отсутствует вовсе, — и подстановка стирала бы с
     * экрана то, чего запрос вообще не касался.
     */
    async updateFields(params, local, errorText) {
      const updated = await crmModel.run(
        () => crmModel.updateLeadFields(this.raw.id, params),
        errorText,
      );
      if (!updated) return;

      this.raw = { ...this.raw, ...local };
    },

    changeServiceType(id) {
      // тип услуги вёрстка берёт развёрнутым объектом — он уже есть в списке
      // типов воронки, запрашивать его ради этого незачем
      const serviceType = this.serviceTypes.find(type => type.id === id) ?? null;

      return this.updateFields(
        { serviceType: id ? crmServiceTypeApi.getIri(id) : null },
        { serviceType },
        'Не удалось сменить тип услуги',
      );
    },

    saveDescription(description) {
      // пустое описание гасим совсем, а не пустой строкой
      return this.updateFields(
        { description: description || null },
        { description: description || null },
        'Не удалось сохранить описание',
      );
    },

    /**
     * Набор вложений целиком. Показываем его сразу, не дожидаясь ответа:
     * только что загруженный файл уже убран из «летящих», и без этого он
     * пропадал бы из списка на время запроса и возвращался с ответом.
     */
    changeFiles(files) {
      this.raw = { ...this.raw, files };

      return this.updateFields(
        { files: files.map(file => file['@id']) },
        { files },
        'Не удалось сохранить вложения',
      );
    },

    /**
     * Напоминания себе по заявке. Попап сам грузит и правит их: заявке они не
     * принадлежат — у неё нет такого поля, а перечитывать её после закрытия
     * незачем.
     */
    openReminders() {
      return popupModel.add({
        wrappable: true,
        component: CrmLeadReminderPopupWrappable,
        params: { leadId: this.raw.id },
      });
    },

    async removeFile(file) {
      const confirmed = await popupModel.addConfirm('Удалить файл?', file.name ?? '');
      if (!confirmed) return;

      await this.changeFiles(this.raw.files.filter(item => item !== file));
    },

    async changeManager() {
      const selected = await popupModel.add({
        wrappable: true,
        component: UserSelectPopupWrappable,
        params: {
          selected: this.raw.manager ? [this.raw.manager['@id']] : [],
          users: this.users,
          title: 'Ответственный',
          emptyTitle: 'Нет доступных сотрудников',
        },
      });
      if (!selected) return;

      const updated = await crmModel.run(
        () => crmModel.updateLeadManager(this.raw.id, selected['@id']),
        'Не удалось сменить ответственного',
      );
      if (!updated) return;

      await this.load();
      this.reloadToken += 1;
    },

    /**
     * Правка клиента заявки.
     *
     * Формы своей здесь нет: и форму, и запрос даёт справочник клиентов — тот
     * же конфиг, по которому клиента заводят при создании заявки (см.
     * CrmPage#leadFields). Зовём модель напрямую, без поля BaseDir: править
     * нужно самого клиента, а поле дало бы ещё и выбор другого со сбросом.
     */
    async editClient() {
      const client = this.raw?.client;
      if (!client) return;

      const updated = await dirModel.edit(this.$options.clientDir, client, {
        context: { owners: await crmModel.clientOwners(this.companyId) },
      });
      // клиент приходит в заявке развёрнутым объектом, так что подменяем его
      // на месте — перечитывать ради этого всю заявку незачем
      if (updated) this.raw.client = updated;
    },

    // Обработчику события аргументы приходят от самого события, а мастеру
    // ниже нужны свои — поэтому отдельный метод, а не рекурсия прямо здесь
    addProject() {
      return this.addProjectSteps();
    },

    /**
     * Создание проекта из заявки в два шага: сначала название и описание
     * универсальной формой, затем отдел и ответственный отдельным попапом —
     * там второе поле включается и наполняется в зависимости от первого.
     *
     * Шаги связаны кнопками «Далее» и «Назад». «Назад» — это проход по тем же
     * шагам заново, поэтому метод зовёт сам себя, передавая уже введённое:
     * при переходах туда-сюда с обоих шагов ничего не теряется.
     */
    async addProjectSteps(projectValues = {}, targetValues = {}) {
      const values = await this.askProjectFields(projectValues);
      if (!values) return;

      const target = await this.askProjectTarget(targetValues);
      if (!target) return;

      if (target.back) {
        await this.addProjectSteps(values, target.values);
        return;
      }

      // алерты о неудаче показывает сама модель: у создания проекта и у
      // назначения ответственного они разные — проект к тому моменту уже создан
      const project = await crmModel.createProjectFromLead({
        ...values,
        ...target,
        lead: crmLeadApi.getIri(this.raw.id),
      });
      if (!project) return;

      await this.load();
    },

    /** Первый шаг: название и описание. */
    askProjectFields(values) {
      const fields = {
        name: form.createFieldObject('text', 'Название проекта', '', { required: 'Укажите название проекта' }),
        description: form.createFieldObject('textarea', 'Описание', ''),
      };

      return popupModel.add({
        wrappable: true,
        component: BaseFormPopupWrappable,
        params: { title: 'Новый проект', fields, values, submitText: 'Далее' },
      });
    },

    /** Второй шаг: отдел и ответственный (что возвращает — см. сам попап). */
    askProjectTarget(values) {
      return popupModel.add({
        wrappable: true,
        component: CrmProjectDepartmentPopupWrappable,
        params: { companyId: this.companyId, values },
      });
    },
  },
};
</script>
